import React from "react";
import { getPullPlan, savePullPlan } from "../../../../api/clientV2";
import type {
  DraftPayload,
  PullTargetChoice,
  TrainState,
} from "../../../../models";
import {
  emptyDraftPayload,
  isPullTargetChoice,
  isSpanGeometry,
  parseDraftPayload,
  readPayloadTrain,
  serializeTrain,
} from "../../../../models";
import type { BarSpan } from "../../WbsBoardView/WbsBoardView";
import type { ActivityInstance, ZoneRow } from "../../draft/types";
import { consolidateZones } from "../helpers";
import type { ProtoNewAct } from "../PullPlanGrid";

type SaveState = "idle" | "saving" | "saved" | "error";

interface DraftArgs {
  scheduleId: number;
  draftId: number | null;
  readOnly: boolean;
  /** Staged ops, owned by the component (read by useDepGraph + many memos that
   *  sit above this hook), threaded through for the autosave payload. */
  pendingOps: DraftPayload;
  setPendingOps: React.Dispatch<React.SetStateAction<DraftPayload>>;
  /** The draft exactly as publish sees it (staged ops + live board geometry). */
  effectiveDraft: DraftPayload;

  // --- Board geometry the autosave serializes -------------------------------
  rows: ZoneRow[];
  instances: ActivityInstance[];
  reviewedAnchors: Set<string>;
  protoNewActs: ProtoNewAct[];
  paletteOrder: string[];
  protoLeafOrder: string[] | null;
  protoPlanWindow: { start: string; end: string } | null;
  protoExtraRows: string[];
  protoRowOverrides: Map<number, string>;
  protoExtraLanes: Map<string, number>;
  protoSpans: Map<number, BarSpan>;
  protoLanes: Map<number, number>;
  leafLevelOverrides: Map<string, string>;
  notaktLeaves: Set<string>;
  protoTrain: TrainState | null;

  // --- Race-guard refs (shared with the draft-reset effect + useUndo) --------
  userEditedRef: React.MutableRefObject<boolean>;
  autosaveSkipFirstRef: React.MutableRefObject<boolean>;
  prevDraftIdRef: React.MutableRefObject<number | null>;

  // --- Setters the load effect drives ---------------------------------------
  resetHistory: () => void;
  setRows: React.Dispatch<React.SetStateAction<ZoneRow[]>>;
  setInstances: React.Dispatch<React.SetStateAction<ActivityInstance[]>>;
  setReviewedAnchors: React.Dispatch<React.SetStateAction<Set<string>>>;
  setProtoNewActs: React.Dispatch<React.SetStateAction<ProtoNewAct[]>>;
  setPaletteOrder: React.Dispatch<React.SetStateAction<string[]>>;
  setProtoLeafOrder: React.Dispatch<React.SetStateAction<string[] | null>>;
  setProtoPlanWindow: React.Dispatch<
    React.SetStateAction<{ start: string; end: string } | null>
  >;
  setProtoExtraRows: React.Dispatch<React.SetStateAction<string[]>>;
  setProtoSpans: React.Dispatch<React.SetStateAction<Map<number, BarSpan>>>;
  setProtoExtraLanes: React.Dispatch<React.SetStateAction<Map<string, number>>>;
  setProtoLanes: React.Dispatch<React.SetStateAction<Map<number, number>>>;
  setProtoRowOverrides: React.Dispatch<React.SetStateAction<Map<number, string>>>;
  setLeafLevelOverrides: React.Dispatch<React.SetStateAction<Map<string, string>>>;
  setNotaktLeaves: React.Dispatch<React.SetStateAction<Set<string>>>;
  setProtoTrain: React.Dispatch<React.SetStateAction<TrainState | null>>;
  setPullTarget: React.Dispatch<React.SetStateAction<PullTargetChoice | null>>;
}

interface DraftApi {
  saveState: SaveState;
  retrySave: () => void;
}

/** The draft spine: the debounced autosave PUT, the once-per-draft saved-payload
 *  load, and the header's save-status pill. The load and autosave race (a
 *  late-resolving fetch must never clobber freshly-staged work) is gated by the
 *  shared `userEditedRef` / `autosaveSkipFirstRef` refs, passed in because the
 *  draft-reset effect and useUndo touch them too. */
export function usePullPlanDraft({
  scheduleId,
  draftId,
  readOnly,
  pendingOps,
  setPendingOps,
  effectiveDraft,
  rows,
  instances,
  reviewedAnchors,
  protoNewActs,
  paletteOrder,
  protoLeafOrder,
  protoPlanWindow,
  protoExtraRows,
  protoRowOverrides,
  protoExtraLanes,
  protoSpans,
  protoLanes,
  leafLevelOverrides,
  notaktLeaves,
  protoTrain,
  userEditedRef,
  autosaveSkipFirstRef,
  prevDraftIdRef,
  resetHistory,
  setRows,
  setInstances,
  setReviewedAnchors,
  setProtoNewActs,
  setPaletteOrder,
  setProtoLeafOrder,
  setProtoPlanWindow,
  setProtoExtraRows,
  setProtoSpans,
  setProtoExtraLanes,
  setProtoLanes,
  setProtoRowOverrides,
  setLeafLevelOverrides,
  setNotaktLeaves,
  setProtoTrain,
  setPullTarget,
}: DraftArgs): DraftApi {
  // Autosave status surfaced in the header.
  const [saveState, setSaveState] = React.useState<SaveState>("idle");

  // Debounced autosave (~800ms) so bar-drag + dropdown clicks coalesce. Persists
  // the board geometry (rows/instances) alongside the effective draft. The PUT's
  // outcome drives the header save pill — a failed save is loud, never silent.
  const saveSeqRef = React.useRef(0);
  const runSave = React.useCallback(
    (payload: string) => {
      if (draftId == null) return;
      const seq = ++saveSeqRef.current;
      setSaveState("saving");
      savePullPlan(scheduleId, draftId, { payload_json: payload })
        .then(() => {
          if (seq === saveSeqRef.current) setSaveState("saved");
        })
        .catch(() => {
          if (seq === saveSeqRef.current) setSaveState("error");
        });
    },
    [scheduleId, draftId],
  );
  const latestPayloadRef = React.useRef<string | null>(null);
  // Payload keys the grid doesn't own (pull_target, entry_partition, …) —
  // captured at load and re-emitted on every autosave so a board edit can't
  // wipe the entry flow's decisions.
  const passthroughRef = React.useRef<Record<string, unknown>>({});
  React.useEffect(() => {
    if (draftId == null) return;
    if (readOnly) return;
    if (autosaveSkipFirstRef.current) {
      autosaveSkipFirstRef.current = false;
      return;
    }
    const payload = JSON.stringify({
      ...passthroughRef.current,
      rows,
      instances,
      anchor_reviews: Array.from(reviewedAnchors),
      proto_board: {
        new_acts: protoNewActs.filter((n) => !n.deleted),
        palette_order: paletteOrder,
        leaf_order: protoLeafOrder ?? undefined,
        plan_window: protoPlanWindow ?? undefined,
        extra_rows: protoExtraRows,
        spans: Object.fromEntries(protoSpans),
        extra_lanes: Object.fromEntries(protoExtraLanes),
        lanes: Object.fromEntries(protoLanes),
        row_overrides: Object.fromEntries(protoRowOverrides),
        leaf_levels: Object.fromEntries(leafLevelOverrides),
        notakt_leaves: Array.from(notaktLeaves),
      },
      train: protoTrain ? serializeTrain(protoTrain) : undefined,
      ...effectiveDraft,
      // Stamp each new activity's CURRENT WBS leaf (the proto tracks its lane
      // through creates AND moves) so publish creates it in the right place
      // instead of collapsing every new activity under the section node.
      pending_new_activities: (effectiveDraft.pending_new_activities ?? []).map((a) => {
        const proto = protoNewActs.find((n) => n.draftId === a.draft_id);
        return proto ? { ...a, wbs_node_id: proto.wbsNodeId } : a;
      }),
    });
    latestPayloadRef.current = payload;
    setSaveState("saving");
    const handle = window.setTimeout(() => runSave(payload), 800);
    return () => window.clearTimeout(handle);
  }, [draftId, scheduleId, rows, instances, readOnly, effectiveDraft, reviewedAnchors, protoNewActs, paletteOrder, protoLeafOrder, protoPlanWindow, protoExtraRows, protoRowOverrides, protoExtraLanes, protoSpans, protoLanes, leafLevelOverrides, notaktLeaves, protoTrain, runSave, autosaveSkipFirstRef]);
  const retrySave = React.useCallback(() => {
    if (latestPayloadRef.current != null) runSave(latestPayloadRef.current);
  }, [runSave]);

  // Load saved draft payload when draftId is set. Replaces the committed seed
  // with the user's last-saved grid edits. Applied AT MOST ONCE per draft, and
  // never over edits the user already made while the fetch was in flight — so a
  // late-resolving load can't clobber freshly-staged work. A brand-new draft's
  // empty payload ({}) is a no-op, leaving the committed seed in place.
  const loadedDraftRef = React.useRef<number | null>(null);
  React.useEffect(() => {
    if (draftId == null) {
      loadedDraftRef.current = null;
      return;
    }
    if (loadedDraftRef.current === draftId) return;
    let cancelled = false;
    getPullPlan(scheduleId, draftId)
      .then((pp) => {
        // Bail if the draft changed, the user already edited (don't clobber), or
        // we already loaded this draft once. Scope is birth data, not user
        // state — merge it even on the edited bail, or the next autosave would
        // permanently de-scope the draft.
        if (cancelled || loadedDraftRef.current === draftId) return;
        let parsed: Record<string, any> | null = null;
        try {
          const raw = JSON.parse(pp.payload_json);
          parsed = raw && typeof raw === "object" ? raw : null;
        } catch {
          parsed = null;
        }
        setProtoTrain(readPayloadTrain(pp.payload_json));
        // Capture keys the grid doesn't own (pull_target, entry_partition, …)
        // so every autosave re-emits them instead of wiping them.
        if (parsed && typeof parsed === "object") {
          const owned = new Set([
            ...Object.keys(emptyDraftPayload()),
            "rows",
            "instances",
            "anchor_reviews",
            "proto_board",
            "train",
          ]);
          const extra: Record<string, unknown> = {};
          for (const [k, v] of Object.entries(parsed)) {
            if (!owned.has(k)) extra[k] = v;
          }
          passthroughRef.current = extra;
          if (isPullTargetChoice(parsed.pull_target)) {
            setPullTarget(parsed.pull_target);
          }
        }
        if (userEditedRef.current) {
          loadedDraftRef.current = draftId;
          const fresh = parseDraftPayload(pp.payload_json);
          if (fresh.scope_wbs_ids.length > 0) {
            setPendingOps((prev) =>
              prev.scope_wbs_ids.length > 0
                ? prev
                : { ...prev, scope_wbs_ids: fresh.scope_wbs_ids },
            );
          }
          return;
        }
        if (!parsed || !Array.isArray(parsed.rows) || !Array.isArray(parsed.instances)) {
          // No board geometry yet — a brand-new draft OR one staged entirely
          // outside the grid (the entry flow's partition + target). Keep the
          // committed seed but hydrate EVERY staged op (deletes, keeps,
          // scope…), or the board would resurrect replaced activities.
          loadedDraftRef.current = draftId;
          autosaveSkipFirstRef.current = true;
          setPendingOps(parseDraftPayload(pp.payload_json));
          return;
        }
        loadedDraftRef.current = draftId;
        autosaveSkipFirstRef.current = true;
        resetHistory();
        // Collapse any legacy duplicate-zone rows into one block per zone.
        const loaded = consolidateZones(parsed.rows, parsed.instances);
        setRows(loaded.rows);
        setInstances(loaded.instances);
        setPendingOps(parseDraftPayload(pp.payload_json));
        if (Array.isArray(parsed.anchor_reviews)) {
          setReviewedAnchors(new Set(parsed.anchor_reviews.filter((k: unknown) => typeof k === "string")));
        }
        const pb = parsed.proto_board;
        if (pb && typeof pb === "object") {
          if (Array.isArray(pb.new_acts)) {
            setProtoNewActs(
              pb.new_acts.filter(
                (n: unknown): n is ProtoNewAct =>
                  !!n &&
                  typeof n === "object" &&
                  typeof (n as { draftId?: unknown }).draftId === "string" &&
                  Array.isArray((n as { wbsPath?: unknown }).wbsPath),
              ),
            );
          }
          if (Array.isArray(pb.palette_order)) {
            setPaletteOrder(
              pb.palette_order.filter((c: unknown) => typeof c === "string"),
            );
          }
          if (Array.isArray(pb.leaf_order)) {
            setProtoLeafOrder(pb.leaf_order.filter((s: unknown) => typeof s === "string"));
          }
          if (
            pb.plan_window &&
            typeof pb.plan_window.start === "string" &&
            typeof pb.plan_window.end === "string"
          ) {
            setProtoPlanWindow({ start: pb.plan_window.start, end: pb.plan_window.end });
          }
          if (Array.isArray(pb.extra_rows)) {
            setProtoExtraRows(pb.extra_rows.filter((s: unknown) => typeof s === "string"));
          }
          if (pb.spans && typeof pb.spans === "object") {
            const m = new Map<number, BarSpan>();
            for (const [k, v] of Object.entries(pb.spans as Record<string, unknown>)) {
              const aid = Number(k);
              if (Number.isFinite(aid) && isSpanGeometry(v)) {
                m.set(aid, { startIndex: v.startIndex, durationDays: v.durationDays });
              }
            }
            setProtoSpans(m);
          }
          if (pb.extra_lanes && typeof pb.extra_lanes === "object") {
            const m = new Map<string, number>();
            for (const [k, v] of Object.entries(pb.extra_lanes as Record<string, unknown>)) {
              if (typeof v === "number" && v > 0) m.set(k, v);
            }
            setProtoExtraLanes(m);
          }
          if (pb.lanes && typeof pb.lanes === "object") {
            const m = new Map<number, number>();
            for (const [k, v] of Object.entries(pb.lanes as Record<string, unknown>)) {
              const aid = Number(k);
              if (Number.isFinite(aid) && typeof v === "number" && v >= 0) m.set(aid, v);
            }
            setProtoLanes(m);
          }
          if (pb.row_overrides && typeof pb.row_overrides === "object") {
            const m = new Map<number, string>();
            for (const [k, v] of Object.entries(pb.row_overrides as Record<string, unknown>)) {
              const aid = Number(k);
              if (Number.isFinite(aid) && typeof v === "string") m.set(aid, v);
            }
            setProtoRowOverrides(m);
          }
          if (pb.leaf_levels && typeof pb.leaf_levels === "object") {
            const m = new Map<string, string>();
            for (const [k, v] of Object.entries(pb.leaf_levels as Record<string, unknown>)) {
              if (typeof v === "string" && v.trim()) m.set(k, v);
            }
            setLeafLevelOverrides(m);
          }
          if (Array.isArray(pb.notakt_leaves)) {
            setNotaktLeaves(
              new Set(pb.notakt_leaves.filter((s: unknown): s is string => typeof s === "string")),
            );
          }
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId, scheduleId, resetHistory]);

  return { saveState, retrySave };
}
